import type { CSSProperties } from "react";
import {
  perpMargin,
  perpPnl,
  predPnl,
  type PerpLeg,
  type PredictionLeg,
} from "../lib/composePayoff";
import type { ExecutionState } from "../hooks/use-composite-execution";
import type { SelectedPerp, SelectedPred } from "../types";

const BRAND = "#9ecbff";
const GOLD = "#f5a623";
const HL_MINT = "#97FCE4";

function abs0Frac(abs: number): number {
  return abs < 10 ? 2 : 0;
}

function signedUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: abs0Frac(Math.abs(n)),
  });
  return neg ? `−${abs}` : `+${abs}`;
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n >= 100 ? 0 : 2,
  });
}

function formatUsdCompact(n: number): string {
  if (!isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function formatEnd(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return "ENDED";
    if (days === 0) return "TODAY";
    if (days === 1) return "1D";
    if (days < 365) return `${days}D`;
    return `${(days / 365).toFixed(1)}Y`;
  } catch {
    return "—";
  }
}

/** Matches the deterministic hue used in predictions-panel.tsx. */
function hueFor(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function cardGradient(seed: string): {
  gradient: string;
  tint: string;
  accent: string;
} {
  const hue = hueFor(seed);
  const accent = `hsl(${hue}, 72%, 64%)`;
  const tint = `hsla(${hue}, 72%, 58%, 0.22)`;
  const gradient = `
    radial-gradient(120% 120% at 0% 0%, hsla(${hue}, 72%, 58%, 0.20), transparent 55%),
    linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
  `;
  return { gradient, tint, accent };
}

const mono9: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 9,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--fg-meta)",
  fontWeight: 600,
};

export function CreateCompositePanel({
  perp,
  pred,
  perpMarket,
  predMarket,
  mark,
  maxGain,
  maxLoss,
  onClose,
  onConfirm,
  executionState,
  walletReady,
}: {
  perp: PerpLeg;
  pred: PredictionLeg;
  perpMarket: SelectedPerp;
  predMarket: SelectedPred;
  mark: number;
  maxGain: number;
  maxLoss: number;
  onClose: () => void;
  onConfirm?: () => void;
  executionState?: ExecutionState;
  walletReady?: boolean;
}) {
  const atMark = perpPnl(perp, mark) + predPnl(pred, mark);
  const notional =
    perp.size * perp.entry + (pred.shares * pred.entryCents) / 100;
  const margin = perpMargin(perp.size, perp.entry, perp.leverage);

  const stage = executionState?.stage ?? "idle";
  const busy =
    stage !== "idle" && stage !== "success" && stage !== "error";
  const succeeded = stage === "success";
  const failed = stage === "error";
  const canConfirm =
    !!onConfirm && walletReady !== false && stage !== "success" && !busy;
  const ctaLabel = !walletReady
    ? "Connect Wallet First"
    : busy
      ? "Executing…"
      : succeeded
        ? "Filled"
        : failed
          ? "Retry Composite"
          : "Confirm & Create";

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        boxShadow: `0 -1px 0 rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)`,
        background: `linear-gradient(180deg, rgba(255,255,255,0.02) 0%, var(--bg-1) 100%)`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid var(--line-2)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            ...mono9,
            color: GOLD,
            letterSpacing: "0.2em",
            fontWeight: 700,
          }}
        >
          NEW COMPOSITE
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel"
          style={{
            width: 20,
            height: 20,
            borderRadius: 2,
            background: "transparent",
            border: "1px solid var(--line-3)",
            color: "var(--fg-meta)",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            padding: 0,
            fontSize: 11,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Body — distributes vertically so content is spread between heroes and numbers */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: "14px 14px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <PerpMiniHero perp={perp} market={perpMarket} margin={margin} />
        <PredMiniHero pred={pred} market={predMarket} />

        {/* Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "2px 0 0",
          }}
        >
          <span style={{ ...mono9, fontSize: 8 }}>COMPOSITE P&amp;L</span>
          <div
            style={{
              flex: 1,
              height: 1,
              background:
                "linear-gradient(90deg, var(--line-2), transparent 90%)",
            }}
          />
        </div>

        {/* Stat grid — taller tiles for more visual weight */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 6,
          }}
        >
          <Stat
            label="AT MARK"
            value={signedUsd(atMark)}
            color={atMark >= 0 ? "var(--up)" : "var(--dn)"}
          />
          <Stat
            label="MAX GAIN"
            value={signedUsd(maxGain)}
            color="var(--up)"
          />
          <Stat
            label="MAX LOSS"
            value={signedUsd(maxLoss)}
            color="var(--dn)"
          />
        </div>

        {/* Totals / settlement block */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            border: "1px dashed var(--line-2)",
            background: "rgba(158, 203, 255, 0.04)",
            overflow: "hidden",
          }}
        >
          <SummaryRow
            label="TOTAL NOTIONAL"
            value={notional.toLocaleString(undefined, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            })}
            emphasis
          />
          <SummaryRow
            label="PERP MARGIN"
            value={signedUsd(margin).replace("+", "")}
          />
          <SummaryRow
            label="PRED PREMIUM"
            value={((pred.shares * pred.entryCents) / 100).toLocaleString(
              undefined,
              {
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 0,
              },
            )}
          />
        </div>
      </div>

      {/* Footer — execution status + CTA pinned to bottom */}
      <div
        style={{
          flexShrink: 0,
          padding: "10px 14px 14px",
          borderTop: "1px solid var(--line-2)",
          background: "var(--bg-1)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <ExecutionStatus state={executionState} walletReady={walletReady} />
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canConfirm}
          style={{
            width: "100%",
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: succeeded ? "var(--up)" : "var(--bg-0)",
            background: succeeded
              ? "transparent"
              : busy
                ? "rgba(158,203,255,0.18)"
                : `linear-gradient(180deg, ${BRAND} 0%, #7ab3ee 100%)`,
            border: succeeded
              ? `1px solid var(--up)`
              : `1px solid ${BRAND}`,
            padding: "12px 14px",
            cursor: canConfirm ? "pointer" : "not-allowed",
            opacity: canConfirm ? 1 : 0.7,
            borderRadius: 2,
            boxShadow: succeeded
              ? "none"
              : `0 0 18px ${BRAND}33, inset 0 1px 0 rgba(255,255,255,0.3)`,
            transition: "opacity 160ms ease, background 160ms ease",
          }}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Execution status — line above the CTA with stage label or error text.
// --------------------------------------------------------------------------

function ExecutionStatus({
  state,
  walletReady,
}: {
  state: ExecutionState | undefined;
  walletReady: boolean | undefined;
}) {
  if (!walletReady) {
    return (
      <StatusLine
        color="var(--fg-dim)"
        dot="var(--fg-dim)"
        label="Wallet not connected"
      />
    );
  }
  const stage = state?.stage ?? "idle";
  if (stage === "idle") {
    return (
      <StatusLine
        color="var(--fg-dim)"
        dot="var(--line-3)"
        label="Ready · two signatures required"
      />
    );
  }
  if (stage === "success") {
    return (
      <StatusLine
        color="var(--up)"
        dot="var(--up)"
        label={`Filled · HL #${state?.hlOrderId ?? "—"} · POLY ${state?.polyOrderId?.slice(0, 8) ?? "—"}…`}
      />
    );
  }
  if (stage === "error") {
    return (
      <StatusLine
        color="var(--dn)"
        dot="var(--dn)"
        label={state?.error ?? "Execution failed"}
        wrap
      />
    );
  }
  return (
    <StatusLine
      color={BRAND}
      dot={BRAND}
      pulse
      label={state?.message ?? "Executing…"}
    />
  );
}

function StatusLine({
  color,
  dot,
  label,
  pulse,
  wrap,
}: {
  color: string;
  dot: string;
  label: string;
  pulse?: boolean;
  wrap?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: wrap ? "flex-start" : "center",
        gap: 8,
        padding: "6px 8px",
        borderRadius: 2,
        background: "rgba(0,0,0,0.25)",
        border: "1px solid var(--line-1)",
        minHeight: 26,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: dot,
          boxShadow: `0 0 8px ${dot}88`,
          flexShrink: 0,
          marginTop: wrap ? 5 : 0,
          animation: pulse ? "hh-status-pulse 1.4s ease-in-out infinite" : undefined,
        }}
      />
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 10,
          letterSpacing: "0.05em",
          color,
          lineHeight: 1.35,
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "clip" : "ellipsis",
          whiteSpace: wrap ? "normal" : "nowrap",
          wordBreak: wrap ? "break-word" : "normal",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------
// Mini hero cards — compact versions of the panel hero cards in the side rails.
// --------------------------------------------------------------------------

function MiniChip({
  label,
  value,
  color,
  accent,
}: {
  label: string;
  value: string;
  color?: string;
  accent?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: "5px 7px",
        border: `1px solid ${accent ? `${accent}33` : "var(--line-1)"}`,
        background: "rgba(0,0,0,0.25)",
        borderRadius: 2,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 7.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--fg-dim)",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 10,
          color: color ?? "var(--fg-hi)",
          fontVariantNumeric: "tabular-nums",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PerpMiniHero({
  perp,
  market,
  margin,
}: {
  perp: PerpLeg;
  market: SelectedPerp;
  margin: number;
}) {
  const chgColor = market.dayChangePct >= 0 ? "var(--up)" : "var(--dn)";
  const sideColor = perp.side === "long" ? "var(--up)" : "var(--dn)";
  return (
    <div
      style={{
        position: "relative",
        padding: "11px 12px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 9,
        borderRadius: 3,
        background: `
          linear-gradient(135deg, ${HL_MINT}0F 0%, transparent 55%),
          linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 100%)
        `,
        border: `1px solid ${HL_MINT}22`,
        boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.3)`,
        overflow: "hidden",
      }}
    >
      {/* Row 1 — icon + coin + max-lev chip + price + change */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <img
          src="/hyperliquid/icon/icon-mintgreen.svg"
          alt=""
          style={{ width: 12, height: 12, flexShrink: 0 }}
        />
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.04em",
            color: "var(--fg-max)",
          }}
        >
          {market.coin}
        </span>
        <span
          style={{
            ...mono9,
            fontSize: 8,
            color: HL_MINT,
            border: `1px solid ${HL_MINT}55`,
            background: `${HL_MINT}10`,
            padding: "1px 5px",
            borderRadius: 2,
            letterSpacing: "0.14em",
          }}
        >
          {market.maxLeverage}× MAX
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--fg-max)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatPrice(market.markPx)}
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: chgColor,
            minWidth: 48,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {market.dayChangePct >= 0 ? "+" : ""}
          {market.dayChangePct.toFixed(2)}%
        </span>
      </div>

      {/* Row 2 — market metric strip (mirrors real hero) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 4,
        }}
      >
        <MiniChip
          label="24H VOL"
          value={formatUsdCompact(market.dayNtlVlm)}
        />
        <MiniChip
          label="OPEN INT"
          value={formatUsdCompact(market.openInterest)}
        />
        <MiniChip
          label="FUNDING 1H"
          value={`${market.fundingPct >= 0 ? "+" : ""}${market.fundingPct.toFixed(3)}%`}
          color={market.fundingPct >= 0 ? "var(--up)" : "var(--dn)"}
        />
      </div>

      {/* Row 3 — compact position summary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 8px",
          borderRadius: 2,
          background: "rgba(0,0,0,0.28)",
          border: "1px solid var(--line-1)",
          boxShadow: `inset 2px 0 0 ${sideColor}`,
        }}
      >
        <span
          style={{
            ...mono9,
            fontSize: 8,
            fontWeight: 700,
            color: sideColor,
            border: `1px solid ${sideColor}55`,
            background: `${sideColor}15`,
            padding: "1px 5px",
            borderRadius: 2,
            letterSpacing: "0.18em",
          }}
        >
          {perp.side.toUpperCase()}
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: "var(--fg-hi)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {perp.size} {market.coin}
        </span>
        <span
          style={{
            marginLeft: "auto",
            ...mono9,
            fontSize: 8,
            color: HL_MINT,
          }}
        >
          {perp.leverage}×
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: "var(--fg-meta)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          @ {formatPrice(perp.entry)}
        </span>
        <span
          style={{
            ...mono9,
            fontSize: 8,
            color: "var(--dn)",
            marginLeft: 2,
          }}
          title={`Margin at risk ${formatPrice(margin)}`}
        >
          · LIQ {formatPrice(perp.liquidation)}
        </span>
      </div>
    </div>
  );
}

function PredMiniHero({
  pred,
  market,
}: {
  pred: PredictionLeg;
  market: SelectedPred;
}) {
  const { gradient, tint, accent } = cardGradient(
    market.category ?? market.id ?? market.question,
  );
  const outcomeColor = pred.outcome === "yes" ? "var(--up)" : "var(--dn)";
  const directionOp = pred.direction === "above" ? "≥" : "≤";
  const yesPct =
    market.yesPrice !== undefined ? (market.yesPrice * 100).toFixed(1) : null;
  const noPct =
    market.noPrice !== undefined ? (market.noPrice * 100).toFixed(1) : null;
  return (
    <div
      style={{
        position: "relative",
        padding: "11px 12px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 9,
        borderRadius: 3,
        background: gradient,
        border: `1px solid ${tint}`,
        boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.3)`,
        overflow: "hidden",
      }}
    >
      <img
        src="/polymarket/logos/icon-blue.svg"
        alt=""
        aria-hidden
        style={{
          position: "absolute",
          right: 8,
          bottom: 8,
          height: 12,
          opacity: 0.4,
          pointerEvents: "none",
        }}
      />
      {/* Row 1 — image + question */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        {market.image ? (
          <img
            src={market.image}
            alt=""
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              objectFit: "cover",
              border: `1px solid ${tint}`,
              borderRadius: 2,
              boxShadow: `0 0 0 2px rgba(0,0,0,0.2)`,
            }}
          />
        ) : (
          <img
            src="/polymarket/logos/icon-blue.svg"
            alt=""
            style={{ width: 24, height: 24, flexShrink: 0, opacity: 0.9 }}
          />
        )}
        <div
          style={{
            fontFamily: "var(--f-sans)",
            fontSize: 11.5,
            color: "var(--fg-max)",
            lineHeight: 1.3,
            minWidth: 0,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          title={market.question}
        >
          {market.question}
        </div>
      </div>

      {/* Row 2 — market metric strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 4,
        }}
      >
        <MiniChip
          label="YES / NO"
          value={`${yesPct ?? "—"}¢ / ${noPct ?? "—"}¢`}
        />
        <MiniChip
          label="24H VOL"
          value={formatUsdCompact(market.volume24hr)}
        />
        <MiniChip
          label="ENDS"
          value={formatEnd(market.endDate)}
          accent={accent}
        />
      </div>

      {/* Row 3 — compact position summary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 8px",
          borderRadius: 2,
          background: "rgba(0,0,0,0.28)",
          border: "1px solid var(--line-1)",
          boxShadow: `inset 2px 0 0 ${outcomeColor}`,
        }}
      >
        <span
          style={{
            ...mono9,
            fontSize: 8,
            fontWeight: 700,
            color: outcomeColor,
            border: `1px solid ${outcomeColor}55`,
            background: `${outcomeColor}15`,
            padding: "1px 5px",
            borderRadius: 2,
            letterSpacing: "0.18em",
          }}
        >
          {pred.outcome.toUpperCase()}
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: "var(--fg-hi)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {pred.shares} @ {pred.entryCents.toFixed(1)}¢
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: "var(--fg-meta)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {directionOp} {formatPrice(pred.threshold)}
        </span>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div
      style={{
        padding: "8px 9px 9px",
        borderRadius: 2,
        border: "1px solid var(--line-1)",
        background: "var(--bg-0)",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 0,
      }}
    >
      <span style={{ ...mono9, fontSize: 8 }}>{label}</span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 13,
          color,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "8px 10px",
        borderBottom: "1px solid var(--line-1)",
      }}
    >
      <span style={{ ...mono9, fontSize: 8, color: "var(--fg-dim)" }}>
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: emphasis ? 12 : 11,
          color: emphasis ? "var(--fg-max)" : "var(--fg-hi)",
          fontVariantNumeric: "tabular-nums",
          fontWeight: emphasis ? 600 : 500,
        }}
      >
        {value}
      </span>
    </div>
  );
}
